import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { AppStore } from '../../store/applicationState';
import { callApi } from '../../utils/callApi';
import { API_ROUTE_PATH } from '../../utils/api_routes';
import { AnimatedClasses } from '../../utils/animatedStyles';
import { ButtonSave, Input, Loader } from '../ui';
import { DefaultSelect, SelectItemType } from '../ui/selects/DefaultSelect';
import { COLORS, COLORS_RGBA } from '../../utils/colors';

interface UKGCredentials {
  service_url: string;
  customer_api_key: string;
  user_api_key: string;
  username: string;
  password: string;
}

interface UKGCompany {
  id: string;
  name: string;
}

interface UKGConnection {
  id: string;
  name: string;
  company_id: string;
  company_name: string;
  base_url: string;
  status: string;
  last_error?: string;
  sync_enabled: boolean;
  local_sync_status: string;
  local_sync_started_at?: string;
  last_sync_at?: string;
  local_sync_completed_at: string;
  created_at: string;
  updated_at: string;
  workspace_id: string;
}

enum Step {
  CREDENTIALS = 'credentials',
  SELECT_COMPANY = 'select_company',
  CONNECTED = 'connected',
}

interface UKGIntegrationProps {}

export const UKGIntegration: React.FC<UKGIntegrationProps> = () => {
  const { Workspaces } = useSelector((store: AppStore) => store);

  const [step, setStep] = React.useState<Step>(Step.CREDENTIALS);
  const [credentials, setCredentials] = React.useState<UKGCredentials>({
    service_url: '',
    customer_api_key: '',
    user_api_key: '',
    username: '',
    password: '',
  });

  const [companies, setCompanies] = React.useState<UKGCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = React.useState<SelectItemType | null>(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = React.useState(false);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [connectedCompanyName, setConnectedCompanyName] = React.useState<string>('');

  const [isLoadingConnection, setIsLoadingConnection] = React.useState(true);
  const [existingConnection, setExistingConnection] = React.useState<UKGConnection | null>(null);

  const workspaceId = Workspaces.current?.id;

  // Fetch existing connection on mount and workspace change
  React.useEffect(() => {
    // Reset state when workspace changes
    setIsLoadingConnection(true);
    setExistingConnection(null);
    setStep(Step.CREDENTIALS);
    setCompanies([]);
    setSelectedCompany(null);
    setError(null);
    setConnectedCompanyName('');

    const fetchExistingConnection = async () => {
      if (!workspaceId) {
        setIsLoadingConnection(false);
        return;
      }

      try {
        const response = await callApi({
          method: 'get',
          path: API_ROUTE_PATH.ukgIntegration.getByWorkspace(workspaceId),
        });

        if (response && response.id) {
          setExistingConnection(response);
        }
      } catch (e) {
        // No existing connection or error - that's ok
        console.log('No existing UKG Pro connection');
      } finally {
        setIsLoadingConnection(false);
      }
    };

    fetchExistingConnection();
  }, [workspaceId]);

  const hasExistingConnection = !!existingConnection?.id;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const normalizeCompanies = (data: any[]): UKGCompany[] => {
    return data
      .map((item) => ({
        id: String(item.id || item.company_id || item.companyId || ''),
        name: String(item.name || item.company_name || item.companyName || item.title || ''),
      }))
      .filter((company) => company.id && company.name);
  };

  const handleGetCompanies = async () => {
    if (!credentials.service_url || !credentials.customer_api_key ||
        !credentials.user_api_key || !credentials.username || !credentials.password) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoadingCompanies(true);
    setError(null);

    try {
      const response = await callApi({
        method: 'post',
        path: API_ROUTE_PATH.ukgIntegration.getCompanies(),
        data: credentials,
      });

      let companiesData: any[] = [];

      if (response && Array.isArray(response)) {
        companiesData = response;
      } else if (response && Array.isArray(response.companies)) {
        companiesData = response.companies;
      } else if (response && Array.isArray(response.data)) {
        companiesData = response.data;
      }

      const normalizedCompanies = normalizeCompanies(companiesData);
      setCompanies(normalizedCompanies);

      if (normalizedCompanies.length === 0) {
        setError('No companies found');
      } else {
        setStep(Step.SELECT_COMPANY);
      }
    } catch (e) {
      console.error('Error fetching companies:', e);
      setError(typeof e === 'string' ? e : 'Failed to fetch companies');
      setCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedCompany) {
      setError('Please select a company');
      return;
    }

    if (!Workspaces.current?.id) {
      setError('No workspace selected');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      const response = await callApi({
        method: 'post',
        path: API_ROUTE_PATH.ukgIntegration.connect(Workspaces.current.id),
        data: {
          service_url: credentials.service_url,
          company_id: selectedCompany.value,
          customer_api_key: credentials.customer_api_key,
          user_api_key: credentials.user_api_key,
          username: credentials.username,
          password: credentials.password,
        },
      });

      setConnectedCompanyName(selectedCompany.title);
      setStep(Step.CONNECTED);

      // Update existing connection state
      if (response && response.id) {
        setExistingConnection(response);
      } else {
        setExistingConnection({
          id: 'new',
          name: '',
          company_id: selectedCompany.value,
          company_name: selectedCompany.title,
          base_url: credentials.service_url,
          status: 'connected',
          sync_enabled: false,
          local_sync_status: 'pending',
          local_sync_completed_at: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          workspace_id: Workspaces.current?.id || '',
        });
      }
    } catch (e) {
      console.error('Error connecting:', e);
      setError(typeof e === 'string' ? e : 'Failed to connect');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleBack = () => {
    setStep(Step.CREDENTIALS);
    setSelectedCompany(null);
    setError(null);
  };

  const companyOptions: SelectItemType[] = companies.map((company) => ({
    id: company.id,
    title: company.name,
    value: company.id,
  }));

  const handleCompanySelect = (item: SelectItemType) => {
    setSelectedCompany(item);
    setError(null);
  };

  if (Workspaces.loading || isLoadingConnection) return <Loader isGreen />;

  const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const formatDate = (dateString?: string) => {
    if (!dateString || dateString === '0001-01-01T00:00:00Z') return null;
    const date = new Date(dateString);
    const month = monthNamesShort[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${month} ${day} ${year}, ${hours}:${minutes}:${seconds}`;
  };

  const isSyncing = existingConnection?.local_sync_status === 'in_progress';

  // If workspace already has UKG Pro connection
  if (hasExistingConnection) {
    return (
      <div className={AnimatedClasses.fadeIn}>
        <WrapperS>
          <TitleS>UKG Pro Integration</TitleS>
          <ConnectedBoxS syncing={isSyncing}>
            <ConnectedIconS syncing={isSyncing}>
              {isSyncing ? <SpinnerS /> : '✓'}
            </ConnectedIconS>
            <ConnectedTextS>
              <strong>{isSyncing ? 'Syncing with UKG Pro' : 'Connected to UKG Pro'}</strong>
              {existingConnection?.name && (
                <InfoRowS>
                  <InfoLabelS>Name:</InfoLabelS>
                  <InfoValueS>{existingConnection.name}</InfoValueS>
                </InfoRowS>
              )}
              <InfoRowS>
                <InfoLabelS>Company:</InfoLabelS>
                <InfoValueS>{existingConnection?.company_name || 'Unknown'}</InfoValueS>
              </InfoRowS>
              <InfoRowS>
                <InfoLabelS>Status:</InfoLabelS>
                <StatusBadgeS status={existingConnection?.status || 'unknown'}>
                  {existingConnection?.status || 'Unknown'}
                </StatusBadgeS>
              </InfoRowS>
              <InfoRowS>
                <InfoLabelS>Sync Status:</InfoLabelS>
                <StatusBadgeS status={existingConnection?.local_sync_status || 'unknown'}>
                  {existingConnection?.local_sync_status === 'in_progress'
                    ? 'In Progress'
                    : existingConnection?.local_sync_status === 'completed'
                    ? 'Completed'
                    : existingConnection?.local_sync_status || 'Unknown'}
                </StatusBadgeS>
              </InfoRowS>
              {existingConnection?.local_sync_status === 'in_progress' && formatDate(existingConnection?.local_sync_started_at) && (
                <InfoRowS>
                  <InfoLabelS>Sync Started:</InfoLabelS>
                  <InfoValueS>{formatDate(existingConnection?.local_sync_started_at)}</InfoValueS>
                </InfoRowS>
              )}
              {(existingConnection?.local_sync_status === 'pending' || existingConnection?.local_sync_status === 'completed') && formatDate(existingConnection?.local_sync_completed_at) && (
                <InfoRowS>
                  <InfoLabelS>Last Successful Sync:</InfoLabelS>
                  <InfoValueS>{formatDate(existingConnection?.local_sync_completed_at)}</InfoValueS>
                </InfoRowS>
              )}
              {existingConnection?.last_error && (
                <ErrorInfoS>
                  <InfoLabelS>Last Error:</InfoLabelS>
                  <span>{existingConnection.last_error}</span>
                </ErrorInfoS>
              )}
            </ConnectedTextS>
          </ConnectedBoxS>
        </WrapperS>
      </div>
    );
  }

  return (
    <div className={AnimatedClasses.fadeIn}>
      <WrapperS>
        <TitleS>UKG Pro Integration</TitleS>
        <DescriptionS>
          Connect your UKG Pro account to sync employee data with OrgVitals.
        </DescriptionS>

        {/* Step indicator */}
        <StepsIndicatorS>
          <StepItemS active={step === Step.CREDENTIALS} completed={step !== Step.CREDENTIALS}>
            <StepNumberS>1</StepNumberS>
            <StepLabelS>Credentials</StepLabelS>
          </StepItemS>
          <StepLineS completed={step !== Step.CREDENTIALS} />
          <StepItemS active={step === Step.SELECT_COMPANY} completed={step === Step.CONNECTED}>
            <StepNumberS>2</StepNumberS>
            <StepLabelS>Select Company</StepLabelS>
          </StepItemS>
          <StepLineS completed={step === Step.CONNECTED} />
          <StepItemS active={step === Step.CONNECTED}>
            <StepNumberS>3</StepNumberS>
            <StepLabelS>Connected</StepLabelS>
          </StepItemS>
        </StepsIndicatorS>

        {/* Step 1: Credentials */}
        {step === Step.CREDENTIALS && (
          <FormS>
            <Input
              label="Service URL"
              name="service_url"
              value={credentials.service_url}
              onChange={handleInputChange}
              placeholder="Enter UKG Pro service URL"
            />

            <Input
              label="Customer API Key"
              name="customer_api_key"
              value={credentials.customer_api_key}
              onChange={handleInputChange}
              placeholder="Enter customer API key"
            />

            <Input
              label="User API Key"
              name="user_api_key"
              value={credentials.user_api_key}
              onChange={handleInputChange}
              placeholder="Enter user API key"
            />

            <Input
              label="Username"
              name="username"
              value={credentials.username}
              onChange={handleInputChange}
              placeholder="Enter username"
            />

            <Input
              label="Password"
              name="password"
              type="password"
              value={credentials.password}
              onChange={handleInputChange}
              placeholder="Enter password"
            />

            {error && <ErrorS>{error}</ErrorS>}

            <ButtonWrapperS>
              <ButtonSave
                title="Next Step"
                onClick={handleGetCompanies}
                loading={isLoadingCompanies}
                disabled={isLoadingCompanies}
                type="button"
              />
            </ButtonWrapperS>
          </FormS>
        )}

        {/* Step 2: Select Company */}
        {step === Step.SELECT_COMPANY && (
          <FormS>
            <DefaultSelect
              label="Select Company"
              data={companyOptions}
              selected={selectedCompany}
              onChange={handleCompanySelect}
              placeHolder="Select a company"
            />

            {error && <ErrorS>{error}</ErrorS>}

            <ButtonRowS>
              <BackButtonS onClick={handleBack}>
                Back
              </BackButtonS>
              <ButtonSave
                title="Connect"
                onClick={handleConnect}
                loading={isConnecting}
                disabled={isConnecting || !selectedCompany}
                type="button"
              />
            </ButtonRowS>
          </FormS>
        )}

        {/* Step 3: Connected */}
        {step === Step.CONNECTED && (
          <ConnectedBoxS>
            <ConnectedIconS>✓</ConnectedIconS>
            <ConnectedTextS>
              <strong>Successfully connected to UKG Pro</strong>
              <span>Company: {connectedCompanyName}</span>
            </ConnectedTextS>
          </ConnectedBoxS>
        )}
      </WrapperS>
    </div>
  );
};

const WrapperS = styled.div`
  max-width: 504px;
  padding-bottom: 40px;
`;

const TitleS = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const DescriptionS = styled.p`
  font-size: 1.4rem;
  color: ${COLORS_RGBA.default(0.6)};
  margin-bottom: 24px;
`;

const FormS = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ButtonWrapperS = styled.div`
  margin-top: 8px;
`;

const ButtonRowS = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const BackButtonS = styled.button`
  padding: 0 28px;
  height: 48px;
  border: 1px solid ${COLORS_RGBA.default(0.2)};
  border-radius: 6px;
  background: white;
  font-size: 1.6rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    border-color: ${COLORS.accent};
    color: ${COLORS.accent};
  }
`;

const ErrorS = styled.div`
  color: #d32f2f;
  font-size: 1.4rem;
  padding: 12px;
  background: rgba(211, 47, 47, 0.1);
  border-radius: 6px;
`;

const StepsIndicatorS = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 32px;
`;

const StepItemS = styled.div<{ active?: boolean; completed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  ${({ active, completed }) => {
    if (active) {
      return `
        color: ${COLORS.accent};
      `;
    }
    if (completed) {
      return `
        color: ${COLORS.accent};
      `;
    }
    return `
      color: ${COLORS_RGBA.default(0.4)};
    `;
  }}
`;

const StepNumberS = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 600;
  border: 2px solid currentColor;
`;

const StepLabelS = styled.span`
  font-size: 1.2rem;
  font-weight: 500;
`;

const StepLineS = styled.div<{ completed?: boolean }>`
  flex: 1;
  height: 2px;
  margin: 0 8px;
  margin-bottom: 24px;
  background: ${({ completed }) => completed ? COLORS.accent : COLORS_RGBA.default(0.2)};
`;

const ConnectedBoxS = styled.div<{ syncing?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  background: ${({ syncing }) => syncing ? 'rgba(25, 118, 210, 0.1)' : 'rgba(46, 125, 50, 0.1)'};
  border-radius: 8px;
  border: 1px solid ${({ syncing }) => syncing ? 'rgba(25, 118, 210, 0.3)' : 'rgba(46, 125, 50, 0.3)'};
`;

const ConnectedIconS = styled.div<{ syncing?: boolean }>`
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 50%;
  background: ${({ syncing }) => syncing ? '#1976d2' : '#2e7d32'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  font-weight: bold;
`;

const SpinnerS = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spinner-spin 1s linear infinite;

  @keyframes spinner-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const ConnectedTextS = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;

  strong {
    font-size: 1.6rem;
    color: #2e7d32;
    margin-bottom: 4px;
  }
`;

const InfoRowS = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.4rem;
  line-height: 1.5;
`;

const InfoLabelS = styled.span`
  color: ${COLORS_RGBA.default(0.6)};
  min-width: 140px;
  flex-shrink: 0;
`;

const InfoValueS = styled.span`
  color: ${COLORS_RGBA.default(0.9)};
  flex: 1;
`;

const StatusBadgeS = styled.span<{ status: string }>`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 1.2rem;
  font-weight: 500;
  text-transform: capitalize;

  ${({ status }) => {
    switch (status) {
      case 'connected':
      case 'completed':
        return `
          background: rgba(46, 125, 50, 0.15);
          color: #2e7d32;
        `;
      case 'syncing':
      case 'in_progress':
        return `
          background: rgba(25, 118, 210, 0.15);
          color: #1976d2;
        `;
      case 'error':
      case 'failed':
        return `
          background: rgba(211, 47, 47, 0.15);
          color: #d32f2f;
        `;
      case 'pending':
        return `
          background: rgba(255, 152, 0, 0.15);
          color: #f57c00;
        `;
      default:
        return `
          background: ${COLORS_RGBA.default(0.1)};
          color: ${COLORS_RGBA.default(0.7)};
        `;
    }
  }}
`;

const ErrorInfoS = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  padding: 8px;
  background: rgba(211, 47, 47, 0.1);
  border-radius: 4px;
  font-size: 1.3rem;
  color: #d32f2f;
`;
